import React from "react";

const GOLD = "#D4AF37", GOLD_LIGHT = "#F5E7B8", GOLD_DEEP = "#8a6a14", RED = "#C41E24", RED_DEEP = "#7d0f14";
const FOIL = `linear-gradient(90deg, ${GOLD_DEEP}, ${GOLD} 30%, ${GOLD_LIGHT} 50%, ${GOLD} 70%, ${GOLD_DEEP})`;

// Laurel branch: leaves placed along an arc (cx, cy, r) from a1..a2 degrees.
const Branch = ({ cx, cy, r, a1, a2, n = 9, flip = false }) => {
  const leaves = [];
  for (let i = 0; i < n; i++) {
    const deg = a1 + ((a2 - a1) * i) / (n - 1);
    const a = deg * (Math.PI / 180);
    const x = cx + r * Math.cos(a), y = cy + r * Math.sin(a);
    const rot = deg + (flip ? 35 : 145);
    const s = 0.78 + 0.5 * (i / (n - 1));
    leaves.push(
      <g key={i} transform={`translate(${x} ${y}) rotate(${rot}) scale(${s})`}>
        <path d="M0 0 C -2.6 -2.2, -2.8 -6.4, 0 -9.6 C 2.8 -6.4, 2.6 -2.2, 0 0 Z" fill="url(#laurelFoil)" stroke={GOLD_DEEP} strokeWidth="0.25" />
        <path d="M0 -0.8 L0 -8.2" stroke={GOLD_DEEP} strokeWidth="0.35" opacity="0.7" />
      </g>
    );
  }
  const p = (a) => [cx + r * Math.cos((a * Math.PI) / 180), cy + r * Math.sin((a * Math.PI) / 180)];
  const [sx, sy] = p(a1), [ex, ey] = p(a2);
  const large = Math.abs(a2 - a1) > 180 ? 1 : 0, sweep = a2 > a1 ? 1 : 0;
  return (
    <g>
      <path d={`M${sx} ${sy} A${r} ${r} 0 ${large} ${sweep} ${ex} ${ey}`} fill="none" stroke="url(#laurelFoil)" strokeWidth="1.1" strokeLinecap="round" />
      {leaves}
    </g>
  );
};

const Defs = () => (
  <defs>
    <linearGradient id="laurelFoil" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor={GOLD_DEEP} /><stop offset="0.45" stopColor={GOLD_LIGHT} /><stop offset="0.7" stopColor={GOLD} /><stop offset="1" stopColor={GOLD_DEEP} /></linearGradient>
    <linearGradient id="ribbonRed" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="#e0262d" /><stop offset="0.5" stopColor={RED} /><stop offset="1" stopColor="#8f1318" /></linearGradient>
    <filter id="lockupShadow" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="1.2" stdDeviation="1.2" floodColor="#000" floodOpacity="0.55" /></filter>
  </defs>
);

// Crest wrapped in gold laurels. Base box: 100 × 78 units (em-scaled by parent fontSize).
export const Emblem = ({ className = "" }) => (
  <div className={`relative ${className}`} style={{ width: "100em", height: "78em" }}>
    <svg viewBox="0 0 100 78" className="absolute inset-0 w-full h-full overflow-visible" style={{ filter: "drop-shadow(0 0.6em 1.4em rgba(212,175,55,0.35))" }}>
      <Defs />
      <Branch cx={50} cy={34} r={40} a1={96} a2={178} n={11} />
      <Branch cx={50} cy={34} r={40} a1={84} a2={2} n={11} flip />
      <circle cx="50" cy="74.5" r="2.4" fill="url(#laurelFoil)" stroke={GOLD_DEEP} strokeWidth="0.3" />
      <path d="M50 72.1 L47.2 76.2 M50 72.1 L52.8 76.2" stroke={GOLD_DEEP} strokeWidth="0.35" />
    </svg>
    <img src="/nexto-logo.png" alt="Next O crest" className="absolute object-contain" style={{ left: "24em", top: "4em", width: "52em", height: "56.6em", filter: "drop-shadow(0 1.5em 2.4em rgba(0,0,0,0.6))" }} />
  </div>
);

const Ribbon = () => (
  <div className="relative" style={{ width: "100em", height: "20em" }}>
    <svg viewBox="0 0 100 20" className="absolute inset-0 w-full h-full overflow-visible">
      <Defs />
      <polygon points="4,6 14,3 14,17 4,20" fill={RED_DEEP} />
      <polygon points="96,6 86,3 86,17 96,20" fill={RED_DEEP} />
      <polygon points="14,3 18,10 14,17" fill="#4a0709" />
      <polygon points="86,3 82,10 86,17" fill="#4a0709" />
      <rect x="14" y="1.5" width="72" height="15.5" fill="url(#ribbonRed)" filter="url(#lockupShadow)" />
      <rect x="15.6" y="3" width="68.8" height="12.5" fill="none" stroke="url(#laurelFoil)" strokeWidth="0.45" />
    </svg>
    <div className="absolute inset-x-0 flex items-center justify-center" style={{ top: "1.5em", height: "15.5em" }}>
      <span className="font-cover uppercase whitespace-nowrap leading-none" style={{ fontSize: "8.4em", letterSpacing: "0.1em", paddingTop: "0.06em", backgroundImage: FOIL, WebkitBackgroundClip: "text", color: "transparent", filter: "drop-shadow(0 0.04em 0 rgba(0,0,0,0.6))" }}>Technical Academy</span>
    </div>
  </div>
);

const Tag = () => (
  <div className="flex items-center justify-center gap-[2.2em] font-mono uppercase whitespace-nowrap" style={{ fontSize: "3.3em", letterSpacing: "0.42em", color: GOLD }}>
    <span className="block" style={{ width: "5em", height: "0.12em", background: `linear-gradient(90deg, transparent, ${GOLD})` }} />
    Next O · Est. 2026
    <span className="block" style={{ width: "5em", height: "0.12em", background: `linear-gradient(90deg, ${GOLD}, transparent)` }} />
  </div>
);

// size = rendered width in px. Stacked: 100 × 116 ratio. Horizontal: 100 × 26 ratio.
export default function AcademyLockup({ variant = "stacked", size = 240, className = "", testId }) {
  const unit = size / 100;
  if (variant === "horizontal") {
    return (
      <div data-testid={testId} className={`relative flex items-center text-white ${className}`} style={{ fontSize: unit, width: "100em", height: "26em", fontFamily: "Montserrat, sans-serif" }}>
        <div style={{ fontSize: "0.31em" }}><Emblem /></div>
        <div className="shrink-0" style={{ width: "0.5em", height: "18em", marginLeft: "1.5em", marginRight: "3em", background: `linear-gradient(180deg, transparent, ${RED}, transparent)`, transform: "skewX(-14deg)" }} />
        <div className="flex flex-col justify-center leading-none">
          <div className="font-mono uppercase" style={{ fontSize: "2.2em", letterSpacing: "0.48em", color: GOLD }}>The Next O · Est. 2026</div>
          <div className="font-cover uppercase whitespace-nowrap" style={{ fontSize: "8.4em", letterSpacing: "0.03em", marginTop: "0.5em", backgroundImage: FOIL, WebkitBackgroundClip: "text", color: "transparent", filter: "drop-shadow(0 0.04em 0 rgba(0,0,0,0.6))" }}>Technical Academy</div>
          <div className="flex items-center gap-[1.5em]" style={{ marginTop: "1.2em" }}>
            <span className="block" style={{ width: "6em", height: "0.28em", background: RED }} />
            <span className="font-mono uppercase text-white/70 whitespace-nowrap" style={{ fontSize: "1.9em", letterSpacing: "0.26em" }}>Elite Technical Training System</span>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div data-testid={testId} className={`relative text-white ${className}`} style={{ fontSize: unit, width: "100em", height: "116em", fontFamily: "Montserrat, sans-serif" }}>
      <Emblem />
      <div style={{ marginTop: "2.5em" }}><Tag /></div>
      <div style={{ marginTop: "2.2em" }}><Ribbon /></div>
    </div>
  );
}
